import { getOwner } from "@ember/application"
import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import analyticsOnly from "cc-frontend/lib/actions/analytics-only"
import type Router from "cc-frontend/router"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import type SessionStore from "cc-frontend/services/session-store"
import type SocketService from "cc-frontend/services/socket"
import { EMAIL_REGEX } from "cc-frontend/utils/email"
import { find, isNil } from "lodash-es"
import { tracked, TrackedObject } from "tracked-built-ins"

interface Args {
  isSignRoute: boolean
  isSignInRoute: boolean
  isSignUpRoute: boolean
  isResetPasswordRoute: boolean
  signUpStep: number
  incorrectPassword: string
  email: string
}

export default class UserSigns extends Component<Args> {
  @service dispatcher!: Dispatcher
  @service socket!: SocketService
  @service session!: Session
  @service finder
  @service appState
  @service rpc!: Rpc
  @service router

  componentId = "user-signs"

  @service declare sessionStore: SessionStore

  // So we can track when we're doing a sign in/up
  @tracked signInProgress = false

  state = new TrackedObject({
    // email & pw
    tempEmail: null as string | null,
    emailConfirmation: null as string | null,
    tempPassword: null as string | null,

    // name & role
    salutation: "Mrs",
    firstName: null as string | null,
    lastName: null as string | null,
    educationRole: null as string | null,

    // planbook
    planbookTitle: "My Awesome Classroom",

    // is...
    isSigningIn: false,
    isSigningUp: false,

    //errors
    incorrectPasswordError: false,
    notMatchingEmailError: false,
    invalidEmailError: false,
    invalidPasswordError: false,
    alreadyCreatedAccountButPasswordsDontMatchError: false,
    enterEmailBeforeResettingPasswordError: false,
    fullNameError: false,
    educationRoleError: false,
    genericError: false,
    userNotFoundError: false,
  })

  checkEmail(email) {
    this.rpc.execute("USER_CHECK_FOR_ACCOUNT", email).then((data) => {
      data.attributes.userExists
        ? this.router.transitionTo("user.sign-in")
        : this.router.transitionTo("user.sign-up")
    })
  }

  validateEmailAndPassword() {
    // dispatch reset errors
    let isValidPassword = (this.state.tempPassword ?? "").length >= 4
    let isValidEmail = this.state.tempEmail !== null && EMAIL_REGEX.test(this.state.tempEmail)
    // let isMatchingEmail = get(this, 'state.tempEmail') === get(this, 'state.emailConfirmation')
    let isMatchingEmail = true

    // dispatch setting errors
    if (isValidEmail === true && isValidPassword === true && isMatchingEmail === true) {
      return true
    } else {
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Form" })
      )
      this.state.incorrectPasswordError = isValidPassword !== true
      this.state.notMatchingEmailError = isMatchingEmail !== true
      this.state.invalidEmailError = isValidEmail !== true
      this.state.invalidPasswordError = false
      return false
    }
  }

  @action goToResetPasswordPage() {
    this.router.transitionTo("user.reset-password")
  }

  @action goToSign() {
    this.router.transitionTo("user.sign")
  }

  @action goToSignUp() {
    this.router.transitionTo("user.sign-up")
  }

  @action resetPassword(e) {
    e.preventDefault()
    let isValidEmailResult = this.state.tempEmail !== null && EMAIL_REGEX.test(this.state.tempEmail)
    if (!isValidEmailResult) {
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Reset Password Error - Email is blank" })
      )
      this.state.enterEmailBeforeResettingPasswordError = true
    } else {
      this.rpc
        .execute("SEND_RESET_PASSWORD_EMAIL", this.state.tempEmail)
        .then((ajaxResult) => {
          if (ajaxResult.type === "error") {
            this._sendError(ajaxResult)
          } else {
            this.router.transitionTo("user.reset-password-confirmation")
          }
        })
        .catch((ajaxResult) => this._sendError(ajaxResult))
    }
  }

  _sendError(ajaxResult) {
    if (ajaxResult.code === "USER_NOT_FOUND") {
      this.state.userNotFoundError = true
    } else {
      this.state.genericError = true
    }
    this.dispatcher.dispatchPreparedAction(
      analyticsOnly({ titleForAnalytics: "User - Reset Password Error - Server Error" })
    )
  }

  @action signInWithGoogleCallback(response) {
    if (response.action === "SIGNED_IN") {
      this.handleSignInResponse(response)
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({
          titleForAnalytics: "User - Sign In",
          context: {
            Method: "Google",
          },
        })
      )
    } else {
      this.handleSignUpResponse(response)
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({
          titleForAnalytics: "User - Sign Up",
          context: {
            Method: "Google",
            value: parseFloat("0"),
            currency: "USD",
          },
        })
      )
    }
  }

  @action
  signIt(event) {
    event.preventDefault()
    this.state.incorrectPasswordError = false
    this.state.invalidEmailError = false
    this.state.invalidPasswordError = false
    this.state.notMatchingEmailError = false

    // Send to analytics
    this.dispatcher.dispatchPreparedAction(
      analyticsOnly({ titleForAnalytics: "User - Sign In or Up" })
    )

    let isValidEmailResult = this.state.tempEmail !== null && EMAIL_REGEX.test(this.state.tempEmail)

    if (isValidEmailResult) {
      this.rpc
        .execute("USER_CHECK_FOR_ACCOUNT", this.state.tempEmail)
        .then((result) => {
          result.attributes.userExists
            ? this.router.transitionTo("user.sign-in")
            : this.router.transitionTo("user.sign-up")
        })
        .catch((result) => {
          console.error("error", result)
          // This wasn't handled in the original implementation, so putting this here
          this.router.transitionTo("user.sign-in")
        })
    } else {
      this.state.invalidEmailError = true
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign In Error - Invalid Email" })
      )
    }
  }

  get inviteId() {
    return this.sessionStore.fetchObject<{ id: string }>("invite")?.id
  }

  get promotionCode() {
    return this.sessionStore.fetchObject("promotionCode") || null
  }

  @action signIn(event) {
    event.preventDefault()
    this.signInProgress = true
    // Because TS isn't able to tell if I do isNil on this.state.tempEmail
    let tempEmail = this.state.tempEmail
    let tempPassword = this.state.tempPassword
    if (this.validateEmailAndPassword() && !isNil(tempEmail) && !isNil(tempPassword)) {
      this.rpc
        .session_authenticate_with_email_and_password({
          sessionId: this.session.id,
          email: tempEmail,
          password: tempPassword,
          inviteId: this.inviteId,
        })
        .then((res) => {
          if (res.responseOk === true) {
            this.handleSignInResponse(res)
            this.dispatcher.dispatchPreparedAction(
              analyticsOnly({
                titleForAnalytics: "User - Sign In",
                context: {
                  Method: "Email and Password",
                },
              })
            )
          } else {
            this.state.incorrectPasswordError = true
            this.state.isSigningIn = false
            this.dispatcher.dispatchPreparedAction(
              analyticsOnly({ titleForAnalytics: "User - Sign In Error - Incorrect Password" })
            )
          }
        })
        .finally(() => {
          this.signInProgress = false
        })
        .catch((res) => {
          console.log("error", res)
        })

      this.state.isSigningIn = true
      this.state.incorrectPasswordError = false
      this.state.invalidEmailError = false
      this.state.invalidPasswordError = false
      this.state.notMatchingEmailError = false
      this.state.alreadyCreatedAccountButPasswordsDontMatchError = false
    } else {
      this.signInProgress = false
    }
  }

  @action
  signUp(event: Event) {
    event.preventDefault()
    this.signInProgress = true
    // so TS doesn't tell us they're null on rpc.user_create. TS can't tell we're checking
    // a nested path :-(
    let tempEmail = this.state.tempEmail
    let tempPassword = this.state.tempPassword

    if (this.validateEmailAndPassword() && !isNil(tempEmail) && !isNil(tempPassword)) {
      let promotionCode = this.promotionCode

      this.state.incorrectPasswordError = false
      this.state.notMatchingEmailError = false
      this.state.invalidEmailError = false
      this.state.invalidPasswordError = false

      this.rpc
        .user_create({
          sessionId: this.session.id,
          email: tempEmail,
          password: tempPassword,
          promotionCode: promotionCode,
          inviteId: this.sessionStore.fetchObject<{ id: string }>("invite")?.id,
        })
        .then((response) => {
          if (response.responseOk === true) {
            this.handleSignUpResponse(response)
            this.dispatcher.dispatchPreparedAction(
              analyticsOnly({
                titleForAnalytics: "User - Sign Up",
                context: {
                  Method: "Email and Password",
                  value: parseFloat("0"),
                  currency: "USD",
                },
              })
            )
          } else if (response.responseOk === false) {
            if (response.result.status === 401) {
              this.state.alreadyCreatedAccountButPasswordsDontMatchError = true
              this.state.isSigningIn = false
              this.dispatcher.dispatchPreparedAction(
                analyticsOnly({ titleForAnalytics: "User - Sign Up Error - User Exists" })
              )
              this.router.transitionTo("user.sign-in")
            } else {
              this.state.genericError = true
              this.state.isSigningUp = false
              this.dispatcher.dispatchPreparedAction(
                analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Server" })
              )
            }
          }
        })
        .finally(() => {
          this.signInProgress = false
        })
        .catch((response) => {
          console.log(response)
        })
    } else {
      this.signInProgress = false
    }
  }

  handleSignUpResponse(response) {
    let user = response.result.data
    let session = find(response.result.included, { type: "session" })
    // if the user has already started, let's fill in the falues
    this.state.firstName = user.attributes.firstName
    this.state.lastName = user.attributes.lastName

    this.finder.insertResponseDocs(
      response.result,
      getOwner(this)!.lookup("adapter:user"),
      "user",
      [user.id]
    )
    this.session.setTokenAndAuthenticate(session)

    // Clear the promotion code and invite from the session store
    this.sessionStore.clear("promotionCode")
    this.sessionStore.clear("invite")

    // Hanndle redirection
    this.redirectOrTransitionAfterSignInOrUp(user)
  }

  handleSignInResponse(res: any) {
    let session = res.result.data
    let user = find(res.result.included, { type: "user" })

    // in case they're still onboarding.
    this.state.firstName = user.attributes.firstName
    this.state.lastName = user.attributes.lastName

    this.finder.insertResponseDocs(res.result, getOwner(this)!.lookup("adapter:user"), "user", [
      user.id,
    ])
    this.session.setTokenAndAuthenticate(session)
    this.session.sendUserToAnalytics()

    // Hanndle redirection
    this.redirectOrTransitionAfterSignInOrUp(user)
  }

  redirectOrTransitionAfterSignInOrUp(user) {
    let urlParams = new URLSearchParams(window.location.search)
    let afterSignInRedirectTo = urlParams.get("afterSignInRedirectTo")

    if (afterSignInRedirectTo) {
      this.socket.beginRedirect()
      // We set this so that we know not to check for unsynced actions
      this.socket.isRedirectingAfterSignIn
      window.location.assign(afterSignInRedirectTo)
    } else if (user?.attributes.onboardingInProgress) {
      let step = user?.attributes.lastOnboardingStep + 1
      console.log("redirectOrTransitionAfterSignInOrUp", step)
      ;(getOwner(this)!.lookup("router:main") as Router).transitionTo("user.sign-up", {
        queryParams: { step: step },
      })
    } else {
      ;(getOwner(this)!.lookup("router:main") as Router).transitionTo("user.show", user.id)
    }
  }

  @action
  updateAccount(event: Event) {
    event.preventDefault()
    let hasFirstName = (this.state.firstName ?? "").length >= 1
    let hasLastName = (this.state.lastName ?? "").length >= 1
    let hasEducationRole = (this.state.educationRole ?? "").length >= 1

    if (hasFirstName !== true || hasLastName !== true || hasEducationRole !== true) {
      this.state.fullNameError = hasFirstName !== true || hasLastName !== true
      this.state.educationRoleError = hasEducationRole !== true
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Account Info" })
      )
    } else {
      // update the account
      this.dispatcher.dispatch("USER_ONBOARDING_UPDATE_ACCOUNT", {
        userId: this.session.userId,
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        salutation: this.state.salutation,
        educationRole: this.state.educationRole,
        lastOnboardingStep: 2,
      })

      // After signup, trigger the user observer so we can update with the first/last name.
      this.session.sendUserToAnalytics()

      // redirect
      // @ts-ignore
      ;(getOwner(this)!.lookup("router:main") as Router).transitionTo({ queryParams: { step: 3 } })
    }
  }

  @action
  moveToCreatePlanbook(event: Event) {
    event.preventDefault()
    // @ts-ignore
    ;(getOwner(this)!.lookup("router:main") as Router).transitionTo({ queryParams: { step: 4 } })
  }

  @action
  updateField(fieldName, val) {
    // I **think** this is called a form is blurred as the component is being torn down
    // on transition to user.show. And then this fires but it's already torn down so
    // we don't have session.id
    if (this.isDestroyed || this.isDestroying) return
    if (val === undefined) return
    this.state[fieldName] = val
  }
}
